import { ID, Task, UpdateTask } from '@griply/types';

import { countedGoalIds } from './links/counted-goal-ids';

export type TaskGoalDeletionAdjustment = {
  taskCount: number;
  completedTaskCount: number;
};

export type BuildTaskDeletionUpdatesOptions = {
  tasks: Task[];
  // Related tasks resolved by id (surviving parents — for childIds pruning;
  // cascade-deleted children — for the onTaskDeleted side-effect). Keyed by id.
  relatedById: Map<ID, Task>;
  updateGoals: boolean;
  deletedAt: Date;
};

export type TaskDeletionUpdates = {
  taskUpdates: UpdateTask[];
  goalAdjustments: Record<ID, TaskGoalDeletionAdjustment>;
  // Ordered list of tasks that were marked deleted (the passed tasks plus any
  // resolved cascade-deleted children) — the caller fires its per-task
  // side-effect (e.g. analytics) over this.
  deletedTasks: Task[];
};

// Ids the deletion needs to resolve before it can build its updates: the
// cascade-deleted children (not themselves selected) and the surviving parent
// of any selected subtask (whose childIds must be pruned).
export const collectRelatedTaskIds = (tasks: Task[]): ID[] => {
  const selectedIds = new Set<ID>(tasks.map(({ id }) => id));
  const relatedIds = new Set<ID>();

  tasks.forEach((task) => {
    task.childIds?.forEach((childId) => {
      if (!selectedIds.has(childId)) {
        relatedIds.add(childId);
      }
    });

    const parentId = task.parentIds?.[task.parentIds.length - 1];
    if (parentId && !selectedIds.has(parentId)) {
      relatedIds.add(parentId);
    }
  });

  return Array.from(relatedIds);
};

export const buildTaskDeletionUpdates = ({
  tasks,
  relatedById,
  updateGoals,
  deletedAt,
}: BuildTaskDeletionUpdatesOptions): TaskDeletionUpdates => {
  const goalAdjustments: Record<ID, TaskGoalDeletionAdjustment> = {};
  if (updateGoals) {
    tasks.forEach((task) => {
      countedGoalIds(task).forEach((goalId) => {
        const current = goalAdjustments[goalId] ?? {
          taskCount: 0,
          completedTaskCount: 0,
        };
        current.taskCount -= 1;
        if (task.completedAt) {
          current.completedTaskCount -= 1;
        }
        goalAdjustments[goalId] = current;
      });
    });
  }

  const selectedIds = new Set<ID>(tasks.map(({ id }) => id));
  const taskUpdates: UpdateTask[] = [];
  const deletedTasks: Task[] = [];
  const childUpdatesSeen = new Set<ID>();
  const parentPrunes = new Map<ID, Set<ID>>();

  tasks.forEach((task) => {
    taskUpdates.push({ id: task.id, deletedAt });
    deletedTasks.push(task);

    task.childIds?.forEach((childId) => {
      if (selectedIds.has(childId) || childUpdatesSeen.has(childId)) {
        return;
      }
      childUpdatesSeen.add(childId);
      taskUpdates.push({ id: childId, deletedAt });
      const childTask = relatedById.get(childId);
      if (childTask) {
        deletedTasks.push(childTask);
      }
    });

    const parentId = task.parentIds?.[task.parentIds.length - 1];
    if (!parentId || selectedIds.has(parentId)) {
      return;
    }
    const existing = parentPrunes.get(parentId) ?? new Set<ID>();
    existing.add(task.id);
    parentPrunes.set(parentId, existing);
  });

  parentPrunes.forEach((removedChildIds, parentId) => {
    const parent = relatedById.get(parentId);
    if (!parent?.childIds?.length) {
      return;
    }
    taskUpdates.push({
      id: parentId,
      childIds: parent.childIds.filter((id) => !removedChildIds.has(id)),
    });
  });

  return { taskUpdates, goalAdjustments, deletedTasks };
};
